// Client-side helpers for fetching accumulation_distribution data directly from
// Azure Blob Storage (trading-data-v2/accumulation_distribution/), using the same
// container-level read+list SAS token as stockMetadataBlob.ts
// (GET /api/watchlist/sas-token). Replaces the previous
// GET /api/accumulation/data/:type and GET /api/accumulation/dates backend
// round-trips. acc_dist.zip is generated by
// processor/src/scheduler_task/2h_accdist.rs.

import { api } from '../services/api';
import { fetchZipEntry } from './zipRangeReader';

const AD_ROOT = 'accumulation_distribution/';

export type AccumulationDistributionType = 'all' | 'foreign' | 'domestic';

export interface AccumulationDistributionResult {
  type: AccumulationDistributionType;
  accumulationData: Record<string, unknown>[];
  total: number;
}

function buildBlobUrl(path: string, containerUrl: string, sasToken: string): string {
  const isDev = (import.meta as any).env?.DEV || false;
  return isDev
    ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
    : `${containerUrl}/${path}?${sasToken}`;
}

function parseAccDistCSV(csvText: string, type: AccumulationDistributionType): AccumulationDistributionResult {
  const lines = csvText.split('\n');
  if (lines.length === 0) return { type, accumulationData: [], total: 0 };
  
  const firstLine = lines[0];
  if (!firstLine) return { type, accumulationData: [], total: 0 };
  const headers = firstLine.split(',').map(h => h.trim());
  const accumulationData: Record<string, unknown>[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const rawLine = lines[i];
    if (!rawLine) continue;
    const line = rawLine.trim();
    if (!line) continue;
    const values = line.split(',');
    
    const obj: Record<string, unknown> = {};
    headers.forEach((col, idx) => {
      let v = values[idx];
      if (col === 'Stock') {
        obj['Ticker'] = String(v ?? '');
      } else {
        obj[col] = Number(v) || 0;
      }
    });
    accumulationData.push(obj);
  }

  return { type, accumulationData, total: accumulationData.length };
}

/**
 * Fetch a file from accumulation_distribution/acc_dist.zip directly from Azure Blob.
 */
export async function fetchAccumulationDistributionData(
  type: AccumulationDistributionType,
  part: 1 | 2 = 1,
): Promise<{ success: true; data: AccumulationDistributionResult }> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error);
    const { containerUrl, sasToken } = tokenRes.data;
    const zipName = part === 2 ? 'acc_dist_part2.zip' : 'acc_dist.zip';
    const zipUrl = buildBlobUrl(`${AD_ROOT}${zipName}`, containerUrl, sasToken);
    
    const buffer = await fetchZipEntry(zipUrl, `acc_dist_${type}.csv`);
    if (!buffer) return { success: true, data: { type, accumulationData: [], total: 0 } };
    
    const text = new TextDecoder().decode(buffer);
    return { success: true, data: parseAccDistCSV(text, type) };
  } catch (err: any) {
    console.error(`Error fetching acc_dist part ${part} data:`, err);
    return { success: true, data: { type, accumulationData: [], total: 0 } };
  }
}

/**
 * Fetch a file from accumulation_distribution/broker_acc_dist/{BROKER}.zip directly from Azure Blob.
 * Broker-specific acc/dist per identity, generated by processor/src/scheduler_task/2h_broker_accdist.rs.
 * When broker is "All" this should not be called — use fetchAccumulationDistributionData instead.
 */
export async function fetchBrokerAccumulationDistributionData(
  broker: string,
  type: AccumulationDistributionType,
  part: 1 | 2 = 1,
): Promise<{ success: true; data: AccumulationDistributionResult }> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error);
    const { containerUrl, sasToken } = tokenRes.data;
    const base = broker.toUpperCase();
    const zipName = part === 2 ? `${base}_part2.zip` : `${base}.zip`;
    const zipUrl = buildBlobUrl(`${AD_ROOT}broker_acc_dist/${zipName}`, containerUrl, sasToken);
    const buffer = await fetchZipEntry(zipUrl, `acc_dist_${type}.csv`);
    if (!buffer) return { success: true, data: { type, accumulationData: [], total: 0 } };
    const text = new TextDecoder().decode(buffer);
    return { success: true, data: parseAccDistCSV(text, type) };
  } catch (err: any) {
    console.error(`Error fetching broker acc_dist ${broker} part ${part}:`, err);
    return { success: true, data: { type, accumulationData: [], total: 0 } };
  }
}

/**
 * Fetch acc_dist_dates.json from accumulation_distribution/acc_dist.zip directly from Azure Blob.
 */
export async function fetchAccumulationDistributionDates(): Promise<{ success: true; data: { dates: string[]; total: number } }> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error);
    const { containerUrl, sasToken } = tokenRes.data;
    const zipUrl = buildBlobUrl(`${AD_ROOT}acc_dist.zip`, containerUrl, sasToken);
    
    const buffer = await fetchZipEntry(zipUrl, 'acc_dist_dates.json');
    if (!buffer) return { success: true, data: { dates: [], total: 0 } };
    
    const text = new TextDecoder().decode(buffer);
    const dates: string[] = text ? (JSON.parse(text)?.dates ?? []) : [];
    return { success: true, data: { dates, total: dates.length } };
  } catch (err: any) {
    console.error('Error fetching acc_dist dates:', err);
    return { success: true, data: { dates: [], total: 0 } };
  }
}
